/* eslint-disable max-len */
/* eslint-disable no-use-before-define */
/* eslint-disable import/no-named-as-default */
/* eslint-disable import/no-named-as-default-member */

import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import utils from '@utils';
import { InvoiceService, ContactService, PaymentService } from '@services';
import { SEND_INVOICE_METHODS, INVOICE_VERSION } from '@constants';
import './PaymentSummaryV2.scss';
import SendInvoiceIcon from '../../../images/send-invoice.svg';
import { DetailSection, HeaderSection } from './components';
import AddInvoice from './modals/AddInvoice/AddInvoice';
import PaymentConfirmation from './modals/PaymentConfirmation/PaymentConfirmation';
import { Tooltip } from '../../../components';
import EditRecipient from './modals/EditRecipient';
import AddClientContactModal from './modals/AddClientContactModal';
import InvoiceErrorsModal from './modals/InvoiceErrorsModal';

const DEFAULT_INVOICE_SUMMARY_TOTALS = {
	OfficerPay       : 0,
	OfficerFees      : 0,
	DepartmentFees   : 0,
	WorkersComp      : 0,
	EquipmentFees    : 0,
	ServiceFee       : 0,
	VehicleFees      : 0,
	OtherFees        : 0,
	CoordinationFees : 0,
	Tax              : 0,
	Subtotal     	 : 0,
	AppliedPayments  : 0,
	Balance		     : 0,
};

const PaymentSummaryV2 = ({
	user,
	flags: { bulkSendingInvoicesV4 },
	router,
}) => {

	const { lang } = global;
	const [invoicesSummary, setInvoicesSummary] = useState([]);
	const [showModalInvoice, setShowModalInvoice] = useState(false);
	const [showSendRecipientsForAll, setSendRecipientsForAll] = useState(false);
	const [invoicesSummaryTotals, setInvoicesSummaryTotals] = useState(DEFAULT_INVOICE_SUMMARY_TOTALS);
	const [showPaymentConfirmationModal, setShowPaymentConfirmationModal] = useState(false);
	const [showAddClientModal, setShowAddClientModal] = useState(false);
	const [contactData, setContactData] = useState({ isLoading : true });
	const [itemToUpdate, setItemToUpdate] = useState();
	const [showInvoiceErrorsModal, setShowInvoiceErrorsModal] = useState(false);
	const [invoiceErrors, setInvoiceErrors] = useState([]);
	const [clientEmailList, setClientEmailList] = useState([]);

	const { push, location: { query: { invoiceIds, method }, state: { fromLocation } = {} } } = router

	const invoiceSplited = invoiceIds.split(',');

	const invoiceItems = invoicesSummary.NonRKPayInvoiceSummaryItems ?? [];
  	const versionSet = useMemo(() => new Set(invoiceItems.map((i) => i.Version)), [invoiceItems]);

	const hasMixedInvoiceVersions = versionSet.size > 1;
	const existsV2BulkSendInvoice = method === SEND_INVOICE_METHODS.GROUP && versionSet.has(INVOICE_VERSION.V2);

	const getInvoices = async () => {
		try {
			const { data } = await InvoiceService.getNonRkPayInvoicesSummary(invoiceSplited);
			setInvoicesSummary(() => ({
				...data,
				NonRKPayInvoiceSummaryItems: data.NonRKPayInvoiceSummaryItems?.map((newItem) => {
					const existingItem = invoicesSummary.NonRKPayInvoiceSummaryItems?.find(
						(oldItem) => oldItem.NonRKPayInvoiceId === newItem.NonRKPayInvoiceId
					);

				return {
						...newItem,
						InvoiceCCRecipients: existingItem?.InvoiceCCRecipients || newItem.InvoiceCCRecipients || [],
						BillingContactName: existingItem?.BillingContactName || newItem.BillingContactName || '',
						BillingContactEmail: existingItem?.ClientEmailAddress || newItem.ClientEmailAddress || '',
						InvoiceBCCRecipients: existingItem?.InvoiceBCCRecipients || newItem.InvoiceBCCRecipients || [],
					};
				})
			}));

			if (method === SEND_INVOICE_METHODS.GROUP) {
				const totals = { ...DEFAULT_INVOICE_SUMMARY_TOTALS };

				data.NonRKPayInvoiceSummaryItems?.forEach((invoice) => {
					totals.OfficerPay += invoice.OfficerPay || 0;
					totals.OfficerFees += invoice.OfficerFees || 0;
					totals.DepartmentFees += invoice.DepartmentFees || 0;
					totals.WorkersComp += invoice.WorkersComp || 0;
					totals.EquipmentFees += invoice.EquipmentFees || 0;
					totals.ServiceFee += invoice.ServiceFee || 0;
					totals.VehicleFees += invoice.VehicleFees || 0;
					totals.OtherFees += invoice.OtherFees || 0;
					totals.CoordinationFees += invoice.CoordinationFees || 0;
					totals.Tax += invoice.Tax || 0;
					totals.Subtotal += invoice.Subtotal || 0;
					totals.Balance += invoice.Balance || 0;
					if (invoice.AppliedPayments) {
						totals.AppliedPayments += invoice.AppliedPayments || 0;
						totals.Subtotal -= invoice.AppliedPayments;
					}
				});

				setInvoicesSummaryTotals(totals);
			}
		} catch (error) {
			utils.notify({
				type    : 'error',
				message : lang.t('Error retrieving invoices summary'),
				timeOut : 1500,
			});
		}
	}

	const goBack = () => {
		if (fromLocation) {
			push(`${fromLocation.pathname}${fromLocation.search}`);
		} else {
			push('/payments/v4/pending-submission');
		}
	}

	useEffect(() => {
		if (invoiceIds?.length > 0) {
			getInvoices();
		} else {
			goBack();
		}
	}, [invoiceIds]);

	useEffect(() => {
		if (invoicesSummary.ClientId) getContacts();
	}, [invoicesSummary])

	useEffect(() => {
		if (invoicesSummary?.NonRKPayInvoiceSummaryItems?.length > 0 &&
			method === SEND_INVOICE_METHODS.GROUP
		) {
			handleMoreThanOneClientEmailAddress();
		}
	}, [invoicesSummary]);

	useEffect(() => {
		if (hasMixedInvoiceVersions) {
			utils.notify({
				type    : 'error',
				message : lang.t('You are sending invoices with different versions'),
				timeOut : 3000,
			});

			push('/payments/v4/pending-submission');
		}

		if (existsV2BulkSendInvoice) {
			utils.notify({
				type    : 'error',
				message : lang.t('BulkV2Invoiceserror'),
				timeOut : 3000,
			});

			push('/payments/v4/pending-submission');
		}
	}, [hasMixedInvoiceVersions, invoiceItems.length, existsV2BulkSendInvoice]);


	const openModalAddInvoice = () => {
		setShowModalInvoice(true);
	}

	const onCancelModalInvoice = () => {
		setShowModalInvoice(false);
	}

	const openModalConfirmationPayment = () => {
		setShowPaymentConfirmationModal(true);
	}

	const openModalUpdateRecipients = (invoice) => {
		setItemToUpdate(invoice);
		setSendRecipientsForAll(true);
	}

	const onCancelModalSendRecipientsForAll = () => {
		setSendRecipientsForAll(false);
	};

	const onCancel = () => {
		setShowPaymentConfirmationModal(false);
	}

	const openInvoiceErrorsModal = (errors) => {
		setInvoiceErrors(errors);
		setShowInvoiceErrorsModal(true);
	}

	const closeInvoiceErrorsModal = () => {
		setInvoiceErrors([]);
		setShowInvoiceErrorsModal(false);
	}

	const updateRecipients = ({
		InvoiceCCRecipients, BillingContactEmail, InvoiceBCCRecipients, SendEmailToMyself
	}) => {
		if (!itemToUpdate) {
			setInvoicesSummary((prevState) => ({
				...prevState,
				NonRKPayInvoiceSummaryItems :
					prevState.NonRKPayInvoiceSummaryItems.map((elm) => ({
						...elm,
						InvoiceCCRecipients,
						BillingContactEmail,
						InvoiceBCCRecipients,
						SendEmailToMyself,
					})),
			}));
		} else {
			setInvoicesSummary((prevState) => ({
				...prevState,
				NonRKPayInvoiceSummaryItems :
					prevState.NonRKPayInvoiceSummaryItems.map((elm) => (
						elm.InvoiceNumber === itemToUpdate.InvoiceNumber ? ({
								...elm,
								InvoiceCCRecipients,
								BillingContactEmail,
								InvoiceBCCRecipients,
								SendEmailToMyself,
							})
						: elm)),
			}));
		}
	}

	/** Retrieve a comprehensive list of contacts for the client (non-paginated) */
	const getContacts = () => {
		setContactData({ isLoading : true });

		ContactService.getAll({ clientId : invoicesSummary.ClientId })
			.then(({ data }) => {
				setContactData({ isLoading : false, data });
			})
			.catch(() => {
				utils.notify({
					type    : 'error',
					message : lang.t('Error loading client contacts'),
					timeOut : 1500,
				});
			});
	};

	const validateInvoicesToSend = () => {
		if (invoiceIds.length) {
			PaymentService.validateNonRKPayInvoicesToSend(invoiceIds).then((data) => {
				if (data) {
					openModalConfirmationPayment();
				}
			})
			.catch((error) => {
				openInvoiceErrorsModal(error.response.data);
			});

		}
	}

	const handleMoreThanOneClientEmailAddress = () => {
		const clientEmails = invoicesSummary.NonRKPayInvoiceSummaryItems.map((item) => item.ClientEmailAddress);
		const uniqueEmails = new Set(clientEmails);

		setClientEmailList(Array.from(uniqueEmails));
	}

	const details = [invoicesSummary.ClientName];
	if (method === SEND_INVOICE_METHODS.GROUP) {
		details.push(<span>{invoicesSummary?.NonRKPayInvoiceSummaryItems?.[0]?.BillingContactEmail}</span>);
		if (invoicesSummary?.NonRKPayInvoiceSummaryItems?.[0]?.InvoiceCCRecipients?.length > 0) {
			details.push(<strong>{lang.t('CC')} : </strong>);
			invoicesSummary?.NonRKPayInvoiceSummaryItems?.[0]?.InvoiceCCRecipients.forEach((email) => {
				details.push(<span style={{ overflowWrap : 'break-word' }}>{email}</span>);
			});
		}
		if (invoicesSummary?.NonRKPayInvoiceSummaryItems?.[0]?.InvoiceBCCRecipients?.length > 0) {
			details.push(<strong>{lang.t('BCC')} : </strong>);
			invoicesSummary?.NonRKPayInvoiceSummaryItems?.[0]?.InvoiceBCCRecipients.forEach((email) => {
				details.push(<span style={{ overflowWrap : 'break-word' }}>{email}</span>);
			});
		}
	}

	return (
		<main id="payment-summary-v2">
			<div className="container">
				<HeaderSection
					title="Send Invoices"
					icon={SendInvoiceIcon}
					goBack={goBack}
					actions={bulkSendingInvoicesV4 ? [
						{ label : 'Add Invoice', action : () => openModalAddInvoice() },
					] : []}
				/>
				<div className="row">
					<div className="col-md-12">
						<div className="information-container flex">
							<DetailSection
								title={lang.t('From')}
								lines={[
									invoicesSummary.OrganizationName,
									invoicesSummary.OrganizationContactName,
									invoicesSummary.OrganizationContactPhoneNumber,
									invoicesSummary.OrganizationContactEmail,
								]}
								align="left"
								wrapperStyles={{ maxWidth : '50%' }}
							/>
							<DetailSection
								title={lang.t('To')}
								lines={details}
								align="right"
								actions={method === SEND_INVOICE_METHODS.GROUP ? [
									{ label : 'Edit Recipients', action : () => openModalUpdateRecipients(invoicesSummary?.NonRKPayInvoiceSummaryItems?.[0]), dataTestId : 'payment-summary-v2__edit-recipients-group' },
								] : []}
								wrapperStyles={{ maxWidth : '50%' }}
								wrapperClasses="hidden-xs hidden-sm hidden-md"
							/>
							<div className="hidden-lg hidden-xl">
								<DetailSection
									title={lang.t('To')}
									lines={details}
									align="right"
									actions={[]}
								/>
							</div>
						</div>
					</div>
					<div className="col-md-12 hidden-lg hidden-xl">
						<DetailSection
							title=""
							lines={[]}
							actions={bulkSendingInvoicesV4 ? [
								{
									label : 'Edit Recipients',
									action : () => openModalUpdateRecipients(invoicesSummary?.NonRKPayInvoiceSummaryItems?.[0]),
									dataTestId : 'payment-summary-v2__edit-recipients-group',
									className  : 'btn-default grow'
								},
								{ label : 'Add Invoice', action : () => openModalAddInvoice(), className  : 'btn-default grow' },
							] : []}
							wrapperStyles={{ flexDirection : 'row' }}
						/>
					</div>
				</div>
				<div className="table-container hidden-xs hidden-sm hidden-md">
					<div className="row invoice-columns">
						<div className="col-sm-2 col-md-2">
							<span className="font-semi-bold">{lang.t('Invoice')} #</span>
						</div>
						<div className="col-sm-3 col-md-3">
							{method === SEND_INVOICE_METHODS.INDIVIDUAL && (
								<span className="font-semi-bold">{lang.t('Invoice Recipients')}</span>
							)}
						</div>
						<div className="col-sm-1 col-md-1">
							<span className="font-semi-bold">{lang.t('Job Date')}</span>
						</div>
						<div className="col-sm-1 col-md-1">
							<span className="font-semi-bold">{lang.t('Due Date')}</span>
						</div>
						<div className="col-sm-3 col-md-3 text-right">
							<span className="font-semi-bold">{lang.t('Fees')}</span>
						</div>
						<div className="col-sm-2 col-md-2 text-right">
							<span className="font-semi-bold">
								{
									bulkSendingInvoicesV4
										? lang.t('Balance')
										: lang.t('Invoice Total')
								}
							</span>
						</div>
					</div>
					<div className="invoice-columns-body" data-test-id="payment-summary-v2-table__body">
						{invoicesSummary?.NonRKPayInvoiceSummaryItems?.length > 0 && invoicesSummary.NonRKPayInvoiceSummaryItems?.map((invoice, index) => {
							const sameJobDay = utils.datesAreOnSameDay(new Date(invoice.JobStartDateRange), new Date(invoice.JobEndDateRange));
							return (
								<div className="row" key={invoice.InvoiceNumber}>
									<div className="col-sm-2 col-md-2">
										<button
											type="button"
											className="btn btn-link"
											onClick={() => push(`/payments/manualinvoice?id=${invoice.NonRKPayInvoiceId}`)}
										>
											<span>{invoice.InvoiceNumber}</span>
										</button>
									</div>
									<div className="col-sm-3 col-md-3">
										{method === SEND_INVOICE_METHODS.INDIVIDUAL && (
											<div className="flex flex-direction-column flex-gap-4">
												{invoice.BillingContactEmail && (
													<div className="truncate">
														<span className="font-semi-bold margin-right-4">{lang.t('To')}:</span>
														<span>{invoice.BillingContactEmail}</span>
													</div>
												)}
												{invoice.InvoiceCCRecipients && invoice.InvoiceCCRecipients.length > 0 && (
													<div>
														<span className="font-semi-bold margin-right-4">{lang.t('CC')}:</span>
														<span>{invoice.InvoiceCCRecipients.join(', ')}</span>
													</div>
												)}
												{invoice.InvoiceBCCRecipients && invoice.InvoiceBCCRecipients.length > 0 && (
													<div>
														<span className="font-semi-bold margin-right-4">{lang.t('BCC')}:</span>
														<span>{invoice.InvoiceBCCRecipients.join(', ')}</span>
													</div>
												)}
												<div className="flex flex-justify-content-start flex-gap-4">
													<button
														type="button"
														className="btn btn-link"
														onClick={() => openModalUpdateRecipients(invoice)}
														data-test-id={`payment-summary-v2__edit-recipients-${index}`}
													>
														{lang.t('Edit Recipients')}
													</button>
												</div>
											</div>
										)}
									</div>
									<div className="col-sm-1 col-md-1">
										<span>{sameJobDay
											? utils.formatDate(invoice.JobStartDateRange, 'en-US', user.TimeZoneId, 'numeric')
											: `${utils.formatDate(invoice.JobStartDateRange, 'en-US', user.TimeZoneId, 'numeric')} - ${utils.formatDate(invoice.JobEndDateRange, 'en-US', user.TimeZoneId, 'numeric')}`}
										</span>
									</div>
									<div className="col-sm-1 col-md-1">
										<span>{utils.formatDate(invoice.InvoiceDueDate, 'en-US', user.TimeZoneId, 'numeric')}</span>
									</div>
									<div className="col-sm-3 col-md-3">
										<div className="flex flex-direction-column flex-justify-content-space-between flex-gap-12">
											<div className="flex flex-direction-column flex-gap-4">
												{invoice?.OfficerPay > 0 && (
													<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
														<div className="fee-item-title text-right">
															<span>{lang.t('Officer Hourly Fees')}:</span>
														</div>
														<div className="fee-item-amount text-right">
															<span>{utils.currencyFormatter(utils.bankersRound(invoice.OfficerPay || 0))}</span>
														</div>
													</div>
												)}
												{invoice?.OfficerFees > 0 && (
													<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
														<div className="fee-item-title text-right">
															<span>{lang.t('Officer Fees')}:</span>
														</div>
														<div className="fee-item-amount text-right">
															<span>{utils.currencyFormatter(utils.bankersRound(invoice.OfficerFees || 0))}</span>
														</div>
													</div>
												)}
												{invoice?.DepartmentFees > 0 && (
													<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
														<div className="fee-item-title text-right">
															<span>{lang.t('Agency Fees')}:</span>
														</div>
														<div className="fee-item-amount text-right">
															<span>{utils.currencyFormatter(utils.bankersRound(invoice.DepartmentFees || 0))}</span>
														</div>
													</div>
												)}
												{invoice?.VehicleFees > 0 && (
													<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
														<div className="fee-item-title text-right">
															<span>{lang.t('Vehicle Fees')}:</span>
														</div>
														<div className="fee-item-amount text-right">
															<span>{utils.currencyFormatter(utils.bankersRound(invoice.VehicleFees || 0))}</span>
														</div>
													</div>
												)}
												{invoice?.EquipmentFees > 0 && (
													<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
														<div className="fee-item-title text-right">
															<span>{lang.t('Equipment Fees')}:</span>
														</div>
														<div className="fee-item-amount text-right">
															<span>{utils.currencyFormatter(utils.bankersRound(invoice.EquipmentFees || 0))}</span>
														</div>
													</div>
												)}
												{invoice?.CoordinationFees > 0 && (
													<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
														<div className="fee-item-title text-right">
															<span>{lang.t('Coordinator Fees')}:</span>
														</div>
														<div className="fee-item-amount text-right">
															<span>{utils.currencyFormatter(utils.bankersRound(invoice.CoordinationFees || 0))}</span>
														</div>
													</div>
												)}
												{invoice?.OtherFees > 0 && (
													<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
														<div className="fee-item-title text-right">
															<span>{lang.t('Other Fees')}:</span>
														</div>
														<div className="fee-item-amount text-right">
															<span>{utils.currencyFormatter(utils.bankersRound(invoice.OtherFees || 0))}</span>
														</div>
													</div>
												)}
												{invoice?.WorkersComp > 0 && (
													<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
														<div className="fee-item-title text-right">
															<span>{lang.t("Workers' Compensation Fees")}:</span>
														</div>
														<div className="fee-item-amount text-right">
															<span>{utils.currencyFormatter(utils.bankersRound(invoice.WorkersComp || 0))}</span>
														</div>
													</div>
												)}
												{invoice?.ServiceFee > 0 && (
													<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
														<div className="fee-item-title text-right">
															<span>{lang.t('Service Fees')}:</span>
														</div>
														<div className="fee-item-amount text-right">
															<span>{utils.currencyFormatter(utils.bankersRound(invoice.ServiceFee || 0))}</span>
														</div>
													</div>
												)}
												{invoice?.Tax > 0 && (
													<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
														<div className="fee-item-title text-right">
															<span>{lang.t('Tax')}:</span>
														</div>
														<div className="fee-item-amount text-right">
															<span>{utils.currencyFormatter(utils.bankersRound(invoice.Tax || 0))}</span>
														</div>
													</div>
												)}
											</div>
										</div>
									</div>
									<div className="col-sm-2 col-md-2 text-right">
										<div className="prepay-and-total">
											<div className="pre">
												{invoice?.IsPrepay && (
													<Tooltip content={lang.t(`This invoice has a prepayment, the current balance is ${invoice.Balance}`)}>
														<div className="prepay-badge" title={lang.t('Prepay')} />
													</Tooltip>
												)}
											</div>
											<span>
												{utils.currencyFormatter(utils.bankersRound(
													bulkSendingInvoicesV4
														? invoice.Balance
														: invoice.Subtotal
												))}
											</span>
										</div>
									</div>
								</div>
							)
						})}
					</div>
					{method === SEND_INVOICE_METHODS.GROUP && (
						<div className="row">
							<div className="col-xl-12 col-lg-12 col-md-12 col-sm-12">
								<div className="totals-container flex flex-direction-column flex-gap-4">
									<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8 margin-bottom-4">
										<div className="fee-item-title text-right">
											<strong>{lang.t('Fee Totals')}</strong>
										</div>
										<div className="fee-item-amount text-right" />
									</div>
									{invoicesSummaryTotals?.OfficerPay > 0 && (
										<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
											<div className="fee-item-title text-right">
												<span>{lang.t('Officer Hourly Fees')}:</span>
											</div>
											<div className="fee-item-amount text-right">
												<span>{utils.currencyFormatter(utils.bankersRound(invoicesSummaryTotals.OfficerPay || 0))}</span>
											</div>
										</div>
									)}
									{invoicesSummaryTotals?.OfficerFees > 0 && (
										<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
											<div className="fee-item-title text-right">
												<span>{lang.t('Officer Fees')}:</span>
											</div>
											<div className="fee-item-amount text-right">
												<span>{utils.currencyFormatter(utils.bankersRound(invoicesSummaryTotals.OfficerFees || 0))}</span>
											</div>
										</div>
									)}
									{invoicesSummaryTotals?.DepartmentFees > 0 && (
										<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
											<div className="fee-item-title text-right">
												<span>{lang.t('Agency Fees')}:</span>
											</div>
											<div className="fee-item-amount text-right">
												<span>{utils.currencyFormatter(utils.bankersRound(invoicesSummaryTotals.DepartmentFees || 0))}</span>
											</div>
										</div>
									)}
									{invoicesSummaryTotals?.VehicleFees > 0 && (
										<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
											<div className="fee-item-title text-right">
												<span>{lang.t('Vehicle Fees')}:</span>
											</div>
											<div className="fee-item-amount text-right">
												<span>{utils.currencyFormatter(utils.bankersRound(invoicesSummaryTotals.VehicleFees || 0))}</span>
											</div>
										</div>
									)}
									{invoicesSummaryTotals?.EquipmentFees > 0 && (
										<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
											<div className="fee-item-title text-right">
												<span>{lang.t('Equipment Fees')}:</span>
											</div>
											<div className="fee-item-amount text-right">
												<span>{utils.currencyFormatter(utils.bankersRound(invoicesSummaryTotals.EquipmentFees || 0))}</span>
											</div>
										</div>
									)}
									{invoicesSummaryTotals?.CoordinationFees > 0 && (
										<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
											<div className="fee-item-title text-right">
												<span>{lang.t('Coordinator Fees')}:</span>
											</div>
											<div className="fee-item-amount text-right">
												<span>{utils.currencyFormatter(utils.bankersRound(invoicesSummaryTotals.CoordinationFees || 0))}</span>
											</div>
										</div>
									)}
									{invoicesSummaryTotals?.OtherFees > 0 && (
										<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
											<div className="fee-item-title text-right">
												<span>{lang.t('Other Fees')}:</span>
											</div>
											<div className="fee-item-amount text-right">
												<span>{utils.currencyFormatter(utils.bankersRound(invoicesSummaryTotals.OtherFees || 0))}</span>
											</div>
										</div>
									)}
									{invoicesSummaryTotals?.WorkersComp > 0 && (
										<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
											<div className="fee-item-title text-right">
												<span>{lang.t("Workers' Compensation Fees")}:</span>
											</div>
											<div className="fee-item-amount text-right">
												<span>{utils.currencyFormatter(utils.bankersRound(invoicesSummaryTotals.WorkersComp || 0))}</span>
											</div>
										</div>
									)}
									<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
										<div className="fee-item-title text-right">
											<span>{lang.t('Service Fees')}:</span>
										</div>
										<div className="fee-item-amount text-right">
											<span>{utils.currencyFormatter(utils.bankersRound(invoicesSummaryTotals.ServiceFee || 0))}</span>
										</div>
									</div>
									<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
										<div className="fee-item-title text-right">
											<span>{lang.t('Tax')}:</span>
										</div>
										<div className="fee-item-amount text-right">
											<span>{utils.currencyFormatter(utils.bankersRound(invoicesSummaryTotals.Tax || 0))}</span>
										</div>
									</div>
									{invoicesSummaryTotals.AppliedPayments !== 0 && (
										<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
											<div className="fee-item-title text-right">
												<span>{lang.t("Applied Payments")}:</span>
											</div>
											<div className="fee-item-amount text-right">
												<span>{utils.currencyFormatter(utils.bankersRound(-invoicesSummaryTotals.AppliedPayments || 0))}</span>
											</div>
										</div>
									)}
									<div className="divider" />
									<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
										<div className="fee-item-title text-right">
											<strong>{lang.t('Grand Total')}:</strong>
										</div>
										<div className="fee-item-amount text-right">
											<strong>
												{utils.currencyFormatter(utils.bankersRound(
													bulkSendingInvoicesV4
														? invoicesSummary.TotalBalance
														: invoicesSummaryTotals.Subtotal
												))}
											</strong>
										</div>
									</div>
								</div>
							</div>
						</div>
					)}
				</div>
				<div className="cards-container hidden-lg hidden-xl margin-top-20">
					{invoicesSummary?.NonRKPayInvoiceSummaryItems?.length > 0 && invoicesSummary.NonRKPayInvoiceSummaryItems?.map((invoice, index) => {
						const sameJobDay = utils.datesAreOnSameDay(new Date(invoice.JobStartDateRange), new Date(invoice.JobEndDateRange));
						return (
							<div key={`${invoice.Id}-${invoice.InvoiceNumber}`}>
								<div className="invoice-card">
									{invoice.IsPrepay && (
										<div className="card-item">
											<div className="pre">
												<Tooltip content={lang.t(`This invoice has a prepayment, the current balance is ${invoice.Balance}`)} placement="right">
													<div className="prepay-badge" title={lang.t('Prepay')} />
												</Tooltip>
											</div>
										</div>
									)}
									<div className="card-item">
										<span className="font-semi-bold">
											{lang.t("Invoice")} #:&nbsp;
										</span>
										<button
											type="button"
											className="btn btn-link"
											onClick={() => push(`/payments/manualinvoice?id=${invoice.NonRKPayInvoiceId}`)}
										>
											<span>{invoice.InvoiceNumber}</span>
										</button>
									</div>
									{method === SEND_INVOICE_METHODS.INDIVIDUAL && (
										<div className="card-item-column">
											<span className="font-semi-bold">
												{lang.t("Invoice Recipients")}:&nbsp;
											</span>
											<div className="flex flex-direction-column flex-gap-4">
												{invoice.BillingContactName && (
													<div className="truncate">
														<span className="font-semi-bold margin-right-4">{lang.t('To')}:</span>
														<span>{invoice.BillingContactEmail}</span>
													</div>
												)}
												{invoice.InvoiceCCRecipients && invoice.InvoiceCCRecipients.length > 0 && (
													<div>
														<span className="font-semi-bold margin-right-4">{lang.t('CC')}:</span>
														<span>{invoice.InvoiceCCRecipients.join(', ')}</span>
													</div>
												)}
												{invoice.InvoiceBCCRecipients && invoice.InvoiceBCCRecipients.length > 0 && (
													<div>
														<span className="font-semi-bold margin-right-4">{lang.t('BCC')}:</span>
														<span>{invoice.InvoiceBCCRecipients.join(', ')}</span>
													</div>
												)}
												<div className="flex flex-justify-content-start flex-gap-4">
													<button
														type="button"
														className="btn btn-link"
														onClick={() => openModalUpdateRecipients(invoice)}
														data-test-id={`payment-summary-v2__edit-recipients-${index}`}
													>
														{lang.t('Edit Recipients')}
													</button>
												</div>
											</div>
										</div>
									)}
									<div className="card-item">
										<span className="font-semi-bold">
											{lang.t("Job Date")}:&nbsp;
										</span>
										<span>{sameJobDay
											? utils.formatDate(invoice.JobStartDateRange, 'en-US', user.TimeZoneId, 'numeric')
											: `${utils.formatDate(invoice.JobStartDateRange, 'en-US', user.TimeZoneId, 'numeric')} - ${utils.formatDate(invoice.JobEndDateRange, 'en-US', user.TimeZoneId, 'numeric')}`}
										</span>
									</div>
									<div className="card-item">
										<span className="font-semi-bold">
											{lang.t("Due Date")}:&nbsp;
										</span>
										<span>{utils.formatDate(invoice.InvoiceDueDate, 'en-US', user.TimeZoneId, 'numeric')}</span>
									</div>
									<div className="card-item-column">
										<span className="font-semi-bold">
											{lang.t("Fees")}:&nbsp;
										</span>
										<div className="flex flex-direction-column flex-justify-content-space-between flex-gap-12 full-width">
											<div className="flex flex-direction-column flex-gap-4">
												{invoice?.OfficerPay > 0 && (
													<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
														<div className="fee-item-title">
															<span>{lang.t('Officer Hourly Fees')}:</span>
														</div>
														<div className="fee-item-amount text-left">
															<span>{utils.currencyFormatter(utils.bankersRound(invoice.OfficerPay || 0))}</span>
														</div>
													</div>
												)}
												{invoice?.OfficerFees > 0 && (
													<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
														<div className="fee-item-title">
															<span>{lang.t('Officer Fees')}:</span>
														</div>
														<div className="fee-item-amount text-left">
															<span>{utils.currencyFormatter(utils.bankersRound(invoice.OfficerFees || 0))}</span>
														</div>
													</div>
												)}
												{invoice?.DepartmentFees > 0 && (
													<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
														<div className="fee-item-title">
															<span>{lang.t('Agency Fees')}:</span>
														</div>
														<div className="fee-item-amount text-left">
															<span>{utils.currencyFormatter(utils.bankersRound(invoice.DepartmentFees || 0))}</span>
														</div>
													</div>
												)}
												{invoice?.VehicleFees > 0 && (
													<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
														<div className="fee-item-title">
															<span>{lang.t('Vehicle Fees')}:</span>
														</div>
														<div className="fee-item-amount text-left">
															<span>{utils.currencyFormatter(utils.bankersRound(invoice.VehicleFees || 0))}</span>
														</div>
													</div>
												)}
												{invoice?.EquipmentFees > 0 && (
													<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
														<div className="fee-item-title">
															<span>{lang.t('Equipment Fees')}:</span>
														</div>
														<div className="fee-item-amount text-left">
															<span>{utils.currencyFormatter(utils.bankersRound(invoice.EquipmentFees || 0))}</span>
														</div>
													</div>
												)}
												{invoice?.CoordinationFees > 0 && (
													<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
														<div className="fee-item-title">
															<span>{lang.t('Coordinator Fees')}:</span>
														</div>
														<div className="fee-item-amount text-left">
															<span>{utils.currencyFormatter(utils.bankersRound(invoice.CoordinationFees || 0))}</span>
														</div>
													</div>
												)}
												{invoice?.OtherFees > 0 && (
													<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
														<div className="fee-item-title">
															<span>{lang.t('Other Fees')}:</span>
														</div>
														<div className="fee-item-amount text-left">
															<span>{utils.currencyFormatter(utils.bankersRound(invoice.OtherFees || 0))}</span>
														</div>
													</div>
												)}
												{invoice?.WorkersComp > 0 && (
													<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
														<div className="fee-item-title">
															<span>{lang.t("Workers' Compensation Fees")}:</span>
														</div>
														<div className="fee-item-amount text-left">
															<span>{utils.currencyFormatter(utils.bankersRound(invoice.WorkersComp || 0))}</span>
														</div>
													</div>
												)}
												{invoice?.ServiceFee > 0 && (
													<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
														<div className="fee-item-title">
															<span>{lang.t('Service Fees')}:</span>
														</div>
														<div className="fee-item-amount text-left">
															<span>{utils.currencyFormatter(utils.bankersRound(invoice.ServiceFee || 0))}</span>
														</div>
													</div>
												)}
												{invoice?.Tax > 0 && (
													<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
														<div className="fee-item-title">
															<span>{lang.t('Tax')}:</span>
														</div>
														<div className="fee-item-amount text-left">
															<span>{utils.currencyFormatter(utils.bankersRound(invoice.Tax || 0))}</span>
														</div>
													</div>
												)}
											</div>
										</div>
									</div>
									<div className="card-item">
										<div className="fee-item-title">
											<span className="font-semi-bold">
												{
													bulkSendingInvoicesV4
														? lang.t('Balance')
														: lang.t('Invoice Total')
												}:
											</span>
										</div>
										<div className="fee-item-amount text-left">
											<span>
												{utils.currencyFormatter(utils.bankersRound(
													bulkSendingInvoicesV4
														? invoice.Balance
														: invoice.Subtotal
												))}
											</span>
										</div>
									</div>
								</div>
							</div>
						)
					})}
					{method === SEND_INVOICE_METHODS.GROUP && (
						<div className="totals-container flex flex-direction-column flex-gap-4">
						<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8 margin-bottom-4">
							<div className="fee-item-title">
								<strong>{lang.t('Fee Totals')}</strong>
							</div>
							<div className="fee-item-amount text-left" />
						</div>
						{invoicesSummaryTotals?.OfficerPay > 0 && (
							<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
								<div className="fee-item-title">
									<span>{lang.t('Officer Hourly Fees')}:</span>
								</div>
								<div className="fee-item-amount text-left">
									<span>{utils.currencyFormatter(utils.bankersRound(invoicesSummaryTotals.OfficerPay || 0))}</span>
								</div>
							</div>
						)}
						{invoicesSummaryTotals?.OfficerFees > 0 && (
							<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
								<div className="fee-item-title">
									<span>{lang.t('Officer Fees')}:</span>
								</div>
								<div className="fee-item-amount text-left">
									<span>{utils.currencyFormatter(utils.bankersRound(invoicesSummaryTotals.OfficerFees || 0))}</span>
								</div>
							</div>
						)}
						{invoicesSummaryTotals?.DepartmentFees > 0 && (
							<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
								<div className="fee-item-title">
									<span>{lang.t('Agency Fees')}:</span>
								</div>
								<div className="fee-item-amount text-left">
									<span>{utils.currencyFormatter(utils.bankersRound(invoicesSummaryTotals.DepartmentFees || 0))}</span>
								</div>
							</div>
						)}
						{invoicesSummaryTotals?.VehicleFees > 0 && (
							<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
								<div className="fee-item-title">
									<span>{lang.t('Vehicle Fees')}:</span>
								</div>
								<div className="fee-item-amount text-left">
									<span>{utils.currencyFormatter(utils.bankersRound(invoicesSummaryTotals.VehicleFees || 0))}</span>
								</div>
							</div>
						)}
						{invoicesSummaryTotals?.EquipmentFees > 0 && (
							<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
								<div className="fee-item-title">
									<span>{lang.t('Equipment Fees')}:</span>
								</div>
								<div className="fee-item-amount text-left">
									<span>{utils.currencyFormatter(utils.bankersRound(invoicesSummaryTotals.EquipmentFees || 0))}</span>
								</div>
							</div>
						)}
						{invoicesSummaryTotals?.CoordinationFees > 0 && (
							<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
								<div className="fee-item-title">
									<span>{lang.t('Coordinator Fees')}:</span>
								</div>
								<div className="fee-item-amount text-left">
									<span>{utils.currencyFormatter(utils.bankersRound(invoicesSummaryTotals.CoordinationFees || 0))}</span>
								</div>
							</div>
						)}
						{invoicesSummaryTotals?.OtherFees > 0 && (
							<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
								<div className="fee-item-title">
									<span>{lang.t('Other Fees')}:</span>
								</div>
								<div className="fee-item-amount text-left">
									<span>{utils.currencyFormatter(utils.bankersRound(invoicesSummaryTotals.OtherFees || 0))}</span>
								</div>
							</div>
						)}
						{invoicesSummaryTotals?.WorkersComp > 0 && (
							<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
								<div className="fee-item-title">
									<span>{lang.t("Workers' Compensation Fees")}:</span>
								</div>
								<div className="fee-item-amount text-left">
									<span>{utils.currencyFormatter(utils.bankersRound(invoicesSummaryTotals.WorkersComp || 0))}</span>
								</div>
							</div>
						)}
						<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
							<div className="fee-item-title">
								<span>{lang.t('Service Fees')}:</span>
							</div>
							<div className="fee-item-amount text-left">
								<span>{utils.currencyFormatter(utils.bankersRound(invoicesSummaryTotals.ServiceFee || 0))}</span>
							</div>
						</div>
						<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
							<div className="fee-item-title">
								<span>{lang.t('Tax')}:</span>
							</div>
							<div className="fee-item-amount text-left">
								<span>{utils.currencyFormatter(utils.bankersRound(invoicesSummaryTotals.Tax || 0))}</span>
							</div>
						</div>
						{invoicesSummaryTotals.AppliedPayments !== 0 && (
							<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
								<div className="fee-item-title">
									<span>{lang.t("Applied Payments")}:</span>
								</div>
								<div className="fee-item-amount text-left">
									<span>{utils.currencyFormatter(utils.bankersRound(invoicesSummaryTotals.AppliedPayments || 0))}</span>
								</div>
							</div>
						)}
						<div className="divider" />
						<div className="flex flex-justify-content-space-between flex-align-items-center flex-gap-8">
							<div className="fee-item-title">
								<strong>{lang.t('Grand Total')}:</strong>
							</div>
							<div className="fee-item-amount text-left">
								<strong>
									{utils.currencyFormatter(utils.bankersRound(
										bulkSendingInvoicesV4
											? invoicesSummary.TotalBalance
											: invoicesSummaryTotals.Subtotal
									))}
								</strong>
							</div>
						</div>
					</div>
					)}
				</div>
				<div className="padding-top-24 flex flex-justify-content-end">
					<button
						type="button"
						className="btn btn-primary hidden-xs hidden-sm hidden-md"
						onClick={validateInvoicesToSend}
					>
						{lang.t('Send')}
					</button>
					<button
						type="button"
						className="btn btn-primary grow hidden-lg hidden-xl"
						onClick={validateInvoicesToSend}
					>
						{lang.t('Send')}
					</button>
				</div>
				<div className="col-md-12 bulk-send-invoice-message-container flex flex-justify-content-end">
					<p>
						{lang.t("Bulk Send Invoices Paid Message_")}
					</p>
				</div>
				<AddInvoice
					show={showModalInvoice}
					onCancel={onCancelModalInvoice}
					clientId={invoicesSummary.ClientId}
					push={push}
					invoiceIds={invoiceIds}
					user={user}
					method={method}
					clientName={invoicesSummary.ClientName}
				/>
				<PaymentConfirmation
					show={showPaymentConfirmationModal}
					onHide={() => setShowPaymentConfirmationModal(false)}
					onCancel={onCancel}
					invoiceItems={invoicesSummary?.NonRKPayInvoiceSummaryItems ?? []}
					router={router}
					method={method}
					onSendCallback={goBack}
				/>

				<EditRecipient
					show={showSendRecipientsForAll}
					onCancel={onCancelModalSendRecipientsForAll}
					clientId={invoicesSummary.ClientId}
					onShowCreateNewClient={() => setShowAddClientModal(true)}
					onCancelCreateNewClient={() => setShowAddClientModal(false)}
					contactData={contactData.data}
					updateRecipients={updateRecipients}
					recipientState={itemToUpdate}
					billToEmail={itemToUpdate?.BillingContactEmail}
					billToEmailList={clientEmailList?.map(email => ({ value: email, label: email }))}
				/>

				{showAddClientModal && (
					<AddClientContactModal
						show={showAddClientModal}
						user={user}
						clientId={invoicesSummary.ClientId}
						organizationId={user.OrganizationId}
						onCancel={() => setShowAddClientModal(false)}
						onSave={() => { setShowAddClientModal(false); getContacts() }}
					/>
				)}

				<InvoiceErrorsModal
					show={showInvoiceErrorsModal}
					onAcknowledge={closeInvoiceErrorsModal}
					invoiceErrors={invoiceErrors}
				/>
			</div>
		</main>
	);
}

PaymentSummaryV2.propTypes = {
	user   : PropTypes.object,
	router : PropTypes.object.isRequired,
	flags  : PropTypes.object.isRequired,
};

PaymentSummaryV2.defaultProps = {
	user : {},
};

export default withLDConsumer()(PaymentSummaryV2);
